// src/components/DynamicBanner.tsx
// Extended Banner component that supports dynamic background colors

import { ReactNode } from "react";
import styled from "styled-components";
import AlertIcon from "@assets/icons/alert.svg";
import CloseIcon from "@assets/icons/cross.svg";
import linkIconUrl from "@assets/icons/link.svg";
import mediaQuery from "@utils/mediaQuery";

interface Props {
  onClose?: () => void;
  children: ReactNode;
  backgroundColor?: string; // NEW: Dynamic background color
  textColor?: string; // NEW: Dynamic text color
}

// Updated Container with dynamic styling
const Container = styled.div<{ $backgroundColor?: string; $textColor?: string }>`
  position: relative;
  border-radius: 12px;
  background: ${props => props.$backgroundColor || 'var(--bg-warning)'}; /* Dynamic or default yellow */
  color: ${props => props.$textColor || 'var(--text-black)'}; /* Dynamic or default black */
  display: flex;
  width: 100%;
  box-sizing: border-box;

  padding: 24px;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px;
  align-self: stretch;

  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-normal);
  line-height: var(--line-height-lg);
  letter-spacing: var(--letter-spacing-normal);

  a {
    color: var(--text-link);
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-bold);
    line-height: var(--line-height-xl);
    text-decoration-line: underline;
    display: inline-flex;
    align-items: center;

    &::after {
      content: "";
      display: inline-block;
      margin-left: 8px;
      width: 16px;
      height: 16px;
      background-image: url(${linkIconUrl});
      background-size: contain;
      background-repeat: no-repeat;
    }
  }

  strong {
    font-weight: var(--font-weight-bold);
  }

  ul {
    padding-left: 24px;
  }

  div {
    flex-wrap: wrap;
  }
`;

const CloseIconButton = styled.button`
  position: relative;
  margin-left: auto;

  ${mediaQuery("tablet")`
  position: absolute;
  top: 24px;
  right: 24px;
  `}
`;

const StyledAlertIcon = styled(AlertIcon)`
  width: 24px;
  height: 24px;
`;

const StyledCloseIcon = styled(CloseIcon)`
  width: 24px;
  height: 24px;
`;

// Enhanced Banner component with dynamic colors
const DynamicBanner = ({ children, onClose, backgroundColor, textColor }: Props) => {
  return (
    <Container $backgroundColor={backgroundColor} $textColor={textColor}>
      <span>
        <StyledAlertIcon />
      </span>
      <div>{children}</div>
      {onClose && (
        <CloseIconButton onClick={onClose}>
          <StyledCloseIcon />
        </CloseIconButton>
      )}
    </Container>
  );
};

export default DynamicBanner;